
import { h, render, Component, cloneElement } from 'preact'
import { useState, useCallback, useRef } from 'preact/hooks'
import GoogleMap from 'google-map-react'
import { fitBounds } from 'google-map-react/utils'
import useSupercluster from 'use-supercluster'

import ServiceStationMarker from './service-station-marker'
import TypeAhead from './type-ahead'
import { getParameter } from '../utils'

const mapStyle = [
    {
        "featureType": "administrative",
        "elementType": "labels.text.fill",
        "stylers": [
            {
                "color": "#444444"
            }
        ]
    },
    {
        "featureType": "landscape",
        "elementType": "all",
        "stylers": [
            {
                "color": "#f2f2f2"
            }
        ]
    },
    {
        "featureType": "poi",
        "elementType": "all",
        "stylers": [
            {
                "visibility": "off"
            }
        ]
    },
    {
        "featureType": "road",
        "elementType": "all",
        "stylers": [
            {
                "saturation": -100
            },
            {
                "lightness": 45
            }
        ]
    },
    {
        "featureType": "road.highway",
        "elementType": "all",
        "stylers": [
            {
                "visibility": "simplified"
            }
        ]
    },
    {
        "featureType": "road.arterial",
        "elementType": "labels.icon",
        "stylers": [
            {
                "visibility": "off"
            }
        ]
    },
    {
        "featureType": "transit",
        "elementType": "all",
        "stylers": [
            {
                "visibility": "off"
            }
        ]
    },
    {
        "featureType": "water",
        "elementType": "all",
        "stylers": [
            {
                "color": "#cfcfcf"
            },
            {
                "visibility": "on"
            }
        ]
    }
]

const boundsForServiceStations = (serviceStations) => {
    const avgLat = serviceStations.reduce((lat, s) => lat + s.lat, 0) / serviceStations.length;
    const avgLng = serviceStations.reduce((lng, s) => lng + s.lng, 0) / serviceStations.length;

    const bounds = serviceStations.reduce((bounds, s) => ({
        nw: { lat: Math.max(bounds.nw.lat, s.lat), lng: Math.min(bounds.nw.lng, s.lng) },
        se: { lat: Math.min(bounds.se.lat, s.lat), lng: Math.max(bounds.se.lng, s.lng)}
    }),{
        nw: { lat: avgLat, lng: avgLng},
        se: { lat: avgLat, lng: avgLng}
    })
    return bounds
}

const boundForServiceStation = (serviceStation, zoom=14) => {
    if (serviceStation) return {
        center: { lat: serviceStation.lat, lng: serviceStation.lng },
        zoom
    }
}

const groupBy = (list, selector) => {
    return list.reduce(function(storage, item) {
        (storage[selector(item)] = storage[selector(item)] || []).push(item);
        return storage;
    }, {});
}

const extractDimension = (list, selector, type) => {
    const groups = groupBy(list, selector)
    return Object.keys(groups).reduce((storage, group) => {
        if (groups[group].length == 1) {
            storage.push({ text: group, lat: groups[group][0].lat, lng: groups[group][0].lng, type })
        }
        else {
            const bounds = boundsForServiceStations(groups[group])
            storage.push({ text: group, bounds: bounds, type})
        }

        return storage
    },[])
}

const extractStates = (serviceStations) => extractDimension(serviceStations, s => s.address.state, 'state')
const extractCities = (serviceStations) => extractDimension(serviceStations, s => s.address.city, 'city')
const extractZipCodes = (serviceStations) => extractDimension(serviceStations, s => s.address.zip, 'zip')

const infoWindowOffset = () => - document.querySelector(".nm-infowindow").offsetHeight / 2

const Marker = (props) => props.children

const SizeProvider = (props) => {
    const [size, setSize] = useState(null);

    const onFirstRender = useCallback(node => {
        if (node) {
            const size = { width: node.offsetWidth, height: node.offsetHeight }
            setSize(size)
        }
    }, [])

    if (!size) return (<div style="height: 100%; width: 100%" ref={onFirstRender}></div>)
    else return cloneElement(props.children, { ...props, ...size })
}

const NearMeMap = (props) => {
    const { apiKey, serviceStations, serviceStationId, idField, idParam, region, language, filterStations, width, height, dataLayer=[] } = props

    const ssId = getParameter(idParam) || serviceStationId
    const visibleStations = (serviceStations || [])
        .filter(s => (filterStations === 'true') && ssId && idField ? s[idField] === ssId : true)
        .map(s => ({ ...s, lat: parseFloat(s.lat, 10), lng: parseFloat(s.lng, 10)}))
        .map((s, i) => s[idField] ? s : ({ ...s, [idField]: `id-${i}`}))
        .map( s => ({
            ...s,
            type: "Feature",
            properties: { cluster: false, ...s },
            geometry: {
                type: "Point",
                coordinates: [s.lng, s.lat]
            }
        }))

    const focusOnStation = visibleStations.find(s => ssId && s[idField] === ssId);

    const mapRef = useRef();
    const [mapSize, setMapSize] = useState({ width, height });
    const [activeStationId, setActiveStationId] = useState(ssId)

    const centerAndZoom = focusOnStation ?
        boundForServiceStation(focusOnStation, zoom) :
        fitBounds(boundsForServiceStations(visibleStations),  mapSize);
    const [center, setCenter] = useState(centerAndZoom.center);
    const [zoom, setZoom] = useState(centerAndZoom.zoom);
    
    const [bounds, setBounds] = useState(boundsForServiceStations(visibleStations))

    const { clusters, supercluster } = useSupercluster({
        points: visibleStations,
        bounds,
        zoom,
        options: { radius: 76, maxZoom: 20, minZoom: 7, minPoints: 4 }
    })

    const typeAheadData = []
        .concat(visibleStations.map(s => ({ lat: s.lat, lng: s.lng, text: [s.name].join(' '), [idField]: s[idField], type: 'station' })))
        .concat(extractStates(visibleStations))
        .concat(extractCities(visibleStations))
        .concat(extractZipCodes(visibleStations))

    const onSelect = (item) => {
        if (item && item.bounds) {
            const centerAndZoom = fitBounds(item.bounds, mapSize)
            mapRef.current.panTo(centerAndZoom.center)
            mapRef.current.setZoom(centerAndZoom.zoom)
            setActiveStationId(null)
        }
        else if (item) {
            mapRef.current.panTo({ lat: item.lat, lng: item.lng})
            if(item[idField]) {
                dataLayer.push({ 
                    "event": "near_me_map_event", 
                    "category": "near_me_map",
                    "action": "select_station",
                    "label": item.name,
                    data: item
                })
                setActiveStationId(item[idField])
                requestAnimationFrame(() => mapRef.current.panBy(0, infoWindowOffset())) 
            }
        }
        
    }

    return (
        <div style="width: 100%; height: 100%; position: relative;">
            <GoogleMap
                bootstrapURLKeys={{ key: apiKey, region: region, language: language }}
                zoom={zoom}
                defaultCenter={center}
                center={center}
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={(({map, maps}) => {
                    mapRef.current = map;
                    const styledMapType = new maps.StyledMapType(mapStyle, {name: 'Styled Map'})
                    map.mapTypes.set("styled_map", styledMapType);
                    map.setMapTypeId('styled_map');
                    if (focusOnStation) {
                        map.panTo({ lat: parseFloat(focusOnStation.lat, 10), lng: parseFloat(focusOnStation.lng, 10)})
                        requestAnimationFrame(() => map.panBy(0, infoWindowOffset()))
                    }
                })}

                onChange={({ zoom, bounds }) => {
                    setZoom(zoom);
                    setBounds([
                        bounds.nw.lng,
                        bounds.se.lat,
                        bounds.se.lng,
                        bounds.nw.lat
                    ]);
                }}
            >
                {   activeStationId &&
                    visibleStations.filter(s => s[idField] === activeStationId).map(s =>
                        <ServiceStationMarker {...s } 
                            active={ true }
                            clearActiveMarker={() => setActiveStationId(null) }
                            setActiveMarker={() => { setActiveStationId(s[idField]); onSelect(s) } }                        
                        />)
                }
                {
                    clusters.map((c, i) => {
                        const [longitude, latitude] = c.geometry.coordinates;
                        
                        const {
                          cluster: isCluster,
                          point_count: pointCount,
                        } = c.properties;
                        if (isCluster) return (
                            <Marker key={`cluster-${c.id}`} lat={latitude} lng={longitude}>
                                <div
                                    class="cluster-marker"
                                    style={{
                                        width: `${10 + (pointCount / visibleStations.length) * 20}px`,
                                        height: `${10 + (pointCount / visibleStations.length) * 20}px`
                                    }}
                                    onClick={() => {
                                      const expansionZoom = Math.min(
                                        supercluster.getClusterExpansionZoom(c.id),
                                        20
                                      );
                                      mapRef.current.setZoom(expansionZoom);
                                      mapRef.current.panTo({ lat: latitude, lng: longitude });
                                    }}
                                >
                                    {pointCount}
                                </div>
                            </Marker>
                        )
                        const s = c.properties
                        if (s[idField] !== activeStationId) return (
                            <ServiceStationMarker {...s}
                                key={i}
                                active={ idField &&  s[idField] == activeStationId }
                                clearActiveMarker={() => setActiveStationId(null) }
                                setActiveMarker={() => { setActiveStationId(s[idField]); onSelect(s) } }
                            />
                        ) 
                    })
                }   
            </GoogleMap>
            <TypeAhead items={typeAheadData} transform={(s) => s.text } onSelect={onSelect} />
        </div>
    )
    
}

const SizedNearMeMap = (props) => (<SizeProvider {...props}><NearMeMap /></SizeProvider>)

export { SizedNearMeMap }
export default NearMeMap
